export interface InvoiceableTaxItem {
  name: string;
  value: number;
}

export interface InvoiceableRetentionItem {
  name: string;
  value: number | string;
}

export interface InvoiceableTaxEventConfig {
  code: string;
  reason: string;
}

export const IGI_TAX_EVENT_CONFIG_MAP: { [taxEvent: string]: InvoiceableTaxEventConfig } = {
  'Art. 44': {
    code: '02',
    reason: 'Operació no subjecta a IGI segons article 44 de la Llei 11/2012 (regles de localització B2B)'
  },
};

export const IGI_TAX_EVENT_REASONS: { [taxEvent: string]: string } = Object.fromEntries(
  Object.entries(IGI_TAX_EVENT_CONFIG_MAP).map(([taxEvent, config]) => [taxEvent, config.reason])
);

export const IVA_TAX_EVENT_CONFIG_MAP: { [taxEvent: string]: InvoiceableTaxEventConfig } = {
  'EX': {
    code: '01',
    reason: 'Operación exenta en virtud del art. 20 de la Ley 37/1992, del IVA.'
  },
  'ISP': {
    code: '01',
    reason: 'Inversión del sujeto pasivo en virtud del art 84.Uno de la Ley 37/1992, del IVA.'
  },
  'NS': {
    code: '02',
    reason: 'Operación no sujeta a IVA en virtud del art. 7 de la Ley 37/1992, del IVA.'
  },
  'NSRL': {
    code: '02',
    reason: 'Operación no sujeta a IVA por reglas de localización en virtud del art. 69 de la Ley 37/1992, del IVA.'
  },
  'INTRA. BIE.': {
    code: '01',
    reason: 'Entrega intracomunitaria exenta en virtud del art. 25 de la Ley 37/1992, del IVA.'
  },
  'INTRA. SER.': {
    code: '01',
    reason: 'Operación no sujeta a IVA en virtud del art. 69 de la Ley 37/1992, del IVA. Inversión del sujeto pasivo conforme a la Directiva 2006/112/CE.'
  },
  'EXPORT.': {
    code: '01',
    reason: 'Operación exenta por exportación en virtud del art. 21 de la Ley 37/1992, del IVA.'
  },
};

export const IGIC_TAX_EVENT_CONFIG_MAP: { [taxEvent: string]: InvoiceableTaxEventConfig } = {
  'Art. 50': {
    code: '01',
    reason: 'Operación exenta de IGIC en virtud del Art. 50 de la Ley 4/2012.'
  },
  'Exención de IGIC (art. 11)': {
    code: '01',
    reason: 'Operación exenta de IGIC en virtud del Art. 11 de la Ley 20/1991.'
  },
  'Art. 12': {
    code: '01',
    reason: 'Operación exenta de IGIC en virtud del Art. 12 de la Ley 20/1991.'
  },
  'Art. 13': {
    code: '01',
    reason: 'Operación exenta de IGIC en virtud del Art. 13 de la Ley 20/1991.'
  },
  'Art. 25': {
    code: '01',
    reason: 'Operación exenta de IGIC en virtud del Art. 25 de la Ley 19/1994.'
  },
  'Art. 47': {
    code: '01',
    reason: 'Operación exenta de IGIC en virtud del Art. 47 de la Ley 19/1994.'
  },
  'Art. 110': {
    code: '01',
    reason: 'Operación exenta de IGIC en virtud del Art. 110 de la Ley 4/2012.'
  },
  'Otros': {
    code: '01',
    reason: 'Operación exenta de IGIC.'
  },
  'Art. 10': {
    code: '01',
    reason: 'Operación exenta de IGIC según lo dispuesto en el Artículo 10 de la Ley 20/1991, de 7 de junio.'
  },
  'Actividades exentas (art. 11)': {
    code: '01',
    reason: 'Operación exenta de IGIC según lo dispuesto en el Artículo 11 de la Ley 20/1991, de 7 de junio.'
  },
  'Envíos fuera de Canarias (art. 11)': {
    code: '01',
    reason: 'Operación exenta de IGIC por exportación según el Artículo 11 de la Ley 20/1991.'
  },
  'Art. 17': {
    code: '02',
    reason: 'Operación no sujeta a IGIC por aplicación de las reglas de localización del Artículo 17 de la Ley 20/1991'
  },
  'Art. 19.1.2º': {
    code: '01',
    reason: 'Operación con Inversión del Sujeto Pasivo conforme al Artículo 19.1.2º de la Ley 20/1991'
  },
  'Art. 109': {
    code: '01',
    reason: 'Operación exenta de IGIC por aplicación del Régimen Especial del Pequeño Empresario o Profesional (REPEP), conforme al Artículo 109 de la Ley 4/2012'
  },
};

export const TAX_EVENT_CONFIG_MAP: { [taxEvent: string]: InvoiceableTaxEventConfig } = {
  ...IVA_TAX_EVENT_CONFIG_MAP,
  ...IGIC_TAX_EVENT_CONFIG_MAP,
  ...IGI_TAX_EVENT_CONFIG_MAP,
  'INTRA.': IVA_TAX_EVENT_CONFIG_MAP['INTRA. BIE.'],
};

export const TAX_TYPE_EVENT_CONFIG_MAP: { [taxType: string]: { [taxEvent: string]: InvoiceableTaxEventConfig } } = {
  iva: IVA_TAX_EVENT_CONFIG_MAP,
  igic: IGIC_TAX_EVENT_CONFIG_MAP,
  igi: IGI_TAX_EVENT_CONFIG_MAP,
};

interface LegacyTaxEventMatcher {
  taxEvent: string;
  reasonFragmentList: string[];
  excludedReasonFragmentList?: string[];
}

const LEGACY_TAX_EVENT_MATCHER_MAP: { [code: string]: LegacyTaxEventMatcher[] } = {
  '01': [
    { taxEvent: 'ISP', reasonFragmentList: ['virtud del art 84'] },
    { taxEvent: 'INTRA. BIE.', reasonFragmentList: ['Ley 37/1992', 'art. 25'] },
    { taxEvent: 'INTRA. SER.', reasonFragmentList: ['Ley 37/1992', 'art. 69'] },
    { taxEvent: 'EXPORT.', reasonFragmentList: ['exportaci'] },
    { taxEvent: 'Art. 50', reasonFragmentList: ['Art. 50'] },
    { taxEvent: 'Art. 110', reasonFragmentList: ['Art. 110'] },
    { taxEvent: 'Exención de IGIC (art. 11)', reasonFragmentList: ['Art. 11'] },
    { taxEvent: 'Art. 12', reasonFragmentList: ['Art. 12'] },
    { taxEvent: 'Art. 13', reasonFragmentList: ['Art. 13'] },
    { taxEvent: 'Art. 25', reasonFragmentList: ['Art. 25'] },
    { taxEvent: 'Art. 47', reasonFragmentList: ['Art. 47'] },
    { taxEvent: 'EX', reasonFragmentList: ['exent'], excludedReasonFragmentList: ['IGIC'] },
    { taxEvent: 'Otros', reasonFragmentList: ['Operación exenta de IGIC.'] },
    { taxEvent: 'Otros', reasonFragmentList: ['Otros'] },
  ],
  '02': [
    { taxEvent: 'NSRL', reasonFragmentList: ['art. 69'] },
    { taxEvent: 'NSRL', reasonFragmentList: ['localización'] },
    { taxEvent: 'NS', reasonFragmentList: [] },
  ],
};

export function getInvoiceableTaxEvent(event: InvoiceableTaxEventConfig | null | undefined): string {
  if (!event) return 'N/A';
  const reason = event.reason.trim().replace(/\.$/, '');
  const taxEvent = Object.keys(TAX_EVENT_CONFIG_MAP).find(key => {
    const config = TAX_EVENT_CONFIG_MAP[key];
    return config.code === event.code && config.reason.replace(/\.$/, '') === reason;
  });
  if (taxEvent) return taxEvent;
  const matcher = (LEGACY_TAX_EVENT_MATCHER_MAP[event.code] || []).find(config =>
    config.reasonFragmentList.every(fragment => reason.includes(fragment)) &&
    !(config.excludedReasonFragmentList || []).some(fragment => reason.includes(fragment))
  );
  return matcher ? matcher.taxEvent : 'N/A';
}

interface InvoiceableTaxConfig {
  defaultRate: number;
  label: string;
  taxItemList: InvoiceableTaxItem[];
}

const DEFAULT_TAX_TYPE = 'iva';

const TAX_SURCHARGE_LABEL_MAP: { [rate: number]: string } = {
  0.005: '4% RE 0.5%',
  0.014: '10% RE 1.4%',
  0.052: '21% RE 5.2%',
};

const DEFAULT_RETENTION_ITEM_LIST: InvoiceableRetentionItem[] = [
  { name: 'N/A', value: 'na' },
  { name: '1%', value: 0.01 },
  { name: '2%', value: 0.02 },
  { name: '7%', value: 0.07 },
  { name: '15%', value: 0.15 },
  { name: '19%', value: 0.19 },
];

const IGI_RETENTION_ITEM_LIST: InvoiceableRetentionItem[] = [
  { name: 'N/A', value: 'na' },
  { name: '1.5%', value: 0.015 },
  { name: '5%', value: 0.05 },
  { name: '10%', value: 0.1 },
];

const INVOICEABLE_TAX_CONFIG_MAP: { [taxType: string]: InvoiceableTaxConfig } = {
  iva: {
    defaultRate: 0.21,
    label: 'IVA',
    taxItemList: [
      { name: '0%', value: 0 },
      { name: '5%', value: 0.05 },
      { name: '21%', value: 0.21 },
      { name: '10%', value: 0.1 },
      { name: '4%', value: 0.04 },
      { name: '21% RE 5.2%', value: 0.052 },
      { name: '10% RE 1.4%', value: 0.014 },
      { name: '4% RE 0.5%', value: 0.005 },
    ],
  },
  igic: {
    defaultRate: 0.07,
    label: 'IGIC',
    taxItemList: [
      { name: '0%', value: 0 },
      { name: '5%', value: 0.05 },
      { name: '20%', value: 0.2 },
      { name: '15%', value: 0.15 },
      { name: '7%', value: 0.07 },
      { name: '3%', value: 0.03 },
    ],
  },
  igi: {
    defaultRate: 0.095,
    label: 'IGI',
    taxItemList: [
      { name: '0%', value: 0 },
      { name: '9.5%', value: 0.095 },
      { name: '4.5%', value: 0.045 },
      { name: '2.5%', value: 0.025 },
      { name: '1%', value: 0.01 },
    ],
  },
};

export function normalizeInvoiceableTaxType(taxType: string | null | undefined): string {
  return taxType && INVOICEABLE_TAX_CONFIG_MAP[taxType] ? taxType : DEFAULT_TAX_TYPE;
}

export function getDefaultInvoiceableTaxRate(taxType: string | null | undefined): number {
  return INVOICEABLE_TAX_CONFIG_MAP[normalizeInvoiceableTaxType(taxType)].defaultRate;
}

export function getInvoiceableTaxLabel(taxType: string | null | undefined): string {
  return INVOICEABLE_TAX_CONFIG_MAP[normalizeInvoiceableTaxType(taxType)].label;
}

export function getInvoiceableTaxItemList(taxType: string | null | undefined): InvoiceableTaxItem[] {
  return INVOICEABLE_TAX_CONFIG_MAP[normalizeInvoiceableTaxType(taxType)].taxItemList;
}

export function getInvoiceableRetentionItemList(taxType: string | null | undefined): InvoiceableRetentionItem[] {
  return normalizeInvoiceableTaxType(taxType) === 'igi' ? IGI_RETENTION_ITEM_LIST : DEFAULT_RETENTION_ITEM_LIST;
}

export function formatInvoiceableTaxRate(rate: number | string | null | undefined): string {
  if (rate === null || rate === undefined || rate === 'na') return '-';
  const numericRate = Number(rate);
  if (Number.isNaN(numericRate)) return '-';
  if (TAX_SURCHARGE_LABEL_MAP[numericRate]) return TAX_SURCHARGE_LABEL_MAP[numericRate];
  return `${Number((numericRate * 100).toFixed(3))}%`;
}
